<template>
  <v-card>
    <v-card-text>
      <v-container>
        <div v-if="isUpdate && loadedItem">
          <supporter-card
            :item="loadedItem"
            :show-actions="false"
            class="mb-4"
          ></supporter-card>
        </div>
        <v-form @submit.prevent="handleSubmit">
          <v-row>
            <v-col md="6" cols="12">
              <form-group
                name="name"
                attribute="fields.name"
                :validator="$v.supporter.name"
              >
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    :loading="fetchLoading"
                    v-model="supporter.name"
                  ></v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col md="6" cols="12">
              <form-group
                name="priority"
                attribute="fields.priority"
                :validator="$v.supporter.priority"
              >
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model="supporter.priority"
                    :loading="fetchLoading"
                    type="number"
                  ></v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col md="12" cols="12">
              <form-group
                name="description"
                attribute="fields.description"
                :validator="$v.supporter.description"
              >
                <template slot-scope="{ attrs, listeners }">
                  <v-textarea
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model="supporter.description"
                    :loading="fetchLoading"
                  ></v-textarea>
                </template>
              </form-group>

              <template>
                <v-switch
                  v-model="supporter.show_banner"
                  :label="$t('labels.show_banner')"
                ></v-switch>

                <!-- Banner and Main Images Inputs -->
                <div class="text-center">
                  <h3 class="mb-2">{{ $t("fields.banner") }}</h3>
                  <span class="mb-2 d-block">{{
                    $t("validation.banner_dimensions")
                  }}</span>
                  <pic-input-wv
                    name="banner"
                    attribute="fields.banner"
                    :validator="$v.supporter.banner"
                    @change="onBannerImageChange"
                    :options="{ width: 600, height: 200 }"
                    class="mb-5"
                  ></pic-input-wv>
                  <h3 class="mb-2">{{ $t("fields.main_image") }}</h3>
                  <pic-input-wv
                    ref="main_image"
                    name="main_image"
                    attribute="fields.main_image"
                    :validator="$v.supporter.main_image"
                    @change="onMainImageChange"
                    :options="{
                      width: 200,
                      height: 200
                    }"
                  ></pic-input-wv>
                </div>
              </template>

              <!------------------------------------>

              <div class="d-flex">
                <v-spacer></v-spacer>
                <v-btn
                  :loading="submitLoading"
                  :disabled="submitLoading || fetchLoading"
                  color="primary"
                  type="submit"
                >
                  {{ $t("button.save") }}
                  <v-icon right dark>mdi-content-save-move-outline</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  updateSupporter,
  fetchSupporter,
  createSupporter
} from "@/api/supporter";
import {
  required,
  minLength,
  maxLength,
  minValue,
  requiredIf
} from "vuelidate/lib/validators";
import { minWords, maxWords } from "@/utils/validate";
import PictureInputWV from "@/components/PictureInputWV";
import SupporterCard from "./SupporterCard";

export default {
  components: {
    "pic-input-wv": PictureInputWV,
    SupporterCard
  },
  props: {
    locale: {
      type: String,
      default: "en"
    },
    isUpdate: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    locale() {
      this.fetchSupporter();
    }
  },
  data() {
    return {
      supporter: {
        name: "",
        description: "",
        main_image: "",
        priority: 1,
        show_banner: false
      },
      // Just a copy of supporter to prevent changes happend to supporter obj affect Card info
      loadedItem: null,
      // Loading States
      submitLoading: false,
      fetchLoading: false
    };
  },
  created() {
    if (this.isUpdate && this.$route.params.slug) {
      this.fetchSupporter();
    }
  },
  methods: {
    fetchSupporter() {
      this.fetchLoading = true;
      const slug = this.$route.params.slug;
      fetchSupporter(slug, this.locale)
        .then(res => {
          this.fetchLoading = false;
          this.supporter = res.data.supporter;
          this.loadedItem = { ...this.supporter };
        })
        .catch(() => {
          this.fetchLoading = false;
        });
    },
    handleSubmit() {
      this.$v.$touch();
      // if Current Validation is invalid return. (dont make request)
      if (this.$v.$invalid) {
        return;
      }
      // Else setLoading state to true then do api request
      this.submitLoading = true;
      this.isUpdate ? this.updateSupporter() : this.createSupporter();
    },
    createSupporter() {
      const reqData = this.initReqData();
      createSupporter(reqData)
        .then(() => {
          this.afterSuccess();
        })
        .catch(() => {
          this.afterError();
        });
    },
    updateSupporter() {
      const slug = this.$route.params.slug;
      const reqData = this.initReqData();
      updateSupporter(slug, reqData)
        .then(() => {
          this.afterSuccess();
        })
        .catch(() => {
          this.afterError();
        });
    },
    // helper function to init our request data object
    initReqData() {
      const reqData = { ...this.supporter, locale: this.locale };
      this.checkBanner(reqData) ? delete reqData.banner : "";
      this.isUpdate ? (reqData._method = "put") : "";
      const formData = new FormData();
      Object.keys(reqData).forEach(key => {
        formData.append(key, reqData[key]);
      });
      return formData;
    },
    checkBanner(reqData) {
      return reqData.banner === null || !(reqData.banner instanceof File);
    },
    afterSuccess() {
      this.submitLoading = false;
      this.$router.push({ name: "supportersList" });
    },
    afterError() {
      this.submitLoading = false;
    },
    onMainImageChange(image) {
      if (this.isUpdate) {
        this.updateMainImage(image);
      } else {
        this.supporter.main_image = image;
      }
    },
    onBannerImageChange(image) {
      this.supporter.banner = image;
    },
    updateMainImage(image) {
      const id = this.loadedItem.main_image.id;
      this.$refs.main_image
        .updateImage(id, image)
        .then(media => {
          this.loadedItem.main_image = media;
        })
        .catch(() => {});
    }
  },
  validations: {
    supporter: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50)
      },
      description: {
        required,
        minWords: minWords(3),
        maxWords: maxWords(50)
      },
      main_image: {
        required: requiredIf(function() {
          return !this.isUpdate;
        })
      },
      banner: {},
      priority: {
        required,
        minValue: minValue(1)
      },
      show_banner: {
        required
      }
    }
  }
};
</script>
